import type { ReactElement } from 'react';
import { useState } from 'react';

import { HeapEvent, HeapCustomEvent } from 'config';
import { hasTouchScreen } from 'utils/device';
import HeapAnalytics from 'services/HeapAnalytics';

import NumberPicker from 'components/shared/NumberPicker';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

type Props = {
  quantity: number;
  minQuantity?: number;
  maxQuantity?: number;
  onChange: (quantity: number) => void;
  skuId: number;
  onRecipePage?: boolean;
  // Made optional until we adjust heap events of the new design
  heapEventTicker?: HeapEvent;
  heapEventTickerAmount?: HeapCustomEvent;
  tooltipErrorMessage?: string;
};

function QuantityPicker({
  quantity,
  minQuantity = 1,
  maxQuantity = 10,
  onRecipePage = false,
  skuId,
  onChange,
  heapEventTicker,
  heapEventTickerAmount,
  tooltipErrorMessage = '',
}: Props): ReactElement {
  const [isTooltipVisible, setTooltipVisible] = useState(false);

  const handlePickerChange = (amount: number) => {
    if (heapEventTickerAmount != null) {
      HeapAnalytics.track(heapEventTickerAmount, { amount });
    }
    onChange(amount);
  };

  const toggleTooltipVisibility = (show?) => {
    if (show === undefined) {
      setTooltipVisible(!isTooltipVisible);
    } else if (!hasTouchScreen) {
      setTooltipVisible(show);
    }
  };

  return (
    <OverlayTrigger
      show={!!tooltipErrorMessage && isTooltipVisible && minQuantity !== 1}
      placement={onRecipePage ? 'bottom' : 'top'}
      overlay={(
        <Tooltip id="tooltip">
          <p>{tooltipErrorMessage}</p>
        </Tooltip>
      )}
    >
      <NumberPicker
        id={`quantity-picker-${skuId}`}
        ariaLabel="Quantity picker"
        value={quantity}
        min={minQuantity}
        max={maxQuantity}
        onChange={handlePickerChange}
        heapEventTicker={heapEventTicker}
        onMinusPointerEnter={() => toggleTooltipVisibility(true)}
        onMinusPointerLeave={() => toggleTooltipVisibility(false)}
        onMinusTouch={() => toggleTooltipVisibility()}
      />
    </OverlayTrigger>
  );
}

export default QuantityPicker;
